import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { testimonials as defaultTestimonials, type Testimonial } from '../../mocks/testimonials';
import { Link } from 'react-router-dom';

interface TestimonialsSectionProps {
  title?: string;
  items?: Testimonial[];
}

export default function TestimonialsSection({ title = 'What Our Clients Say', items = defaultTestimonials }: TestimonialsSectionProps) {
  const [featured, setFeatured] = useState(0);
  const { ref, isVisible } = useScrollAnimation();

  const displayItems = items.slice(0, 4);

  return (
    <section className="py-24 bg-[#03143A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://public.readdy.ai/ai/img_res/96421355544f65ca3310971514eda4c0.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C70532]/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div ref={ref} className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-14 gap-6">
            <div>
              <span className="inline-block px-5 py-2 bg-white/10 text-white/80 text-xs font-bold uppercase tracking-widest rounded-full mb-5">Client Testimonials</span>
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">{title}</h2>
            </div>
            <div className="flex flex-col items-start md:items-end gap-2">
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5].map(i => <i key={i} className="ri-star-fill text-amber-400 text-lg" />)}
                <span className="text-white font-black text-xl ml-1">4.8</span>
              </div>
              <p className="text-white/50 text-sm">150+ verified Google reviews</p>
              <p className="text-white/50 text-sm">98% client satisfaction rate</p>
            </div>
          </div>

          {/* Featured testimonial */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-10 border border-white/10 mb-8">
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-2xl bg-[#C70532] flex items-center justify-center">
                  <span className="text-white font-black text-xl">{displayItems[featured]?.initials}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-0.5 mb-3">
                  {Array.from({ length: displayItems[featured]?.rating ?? 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-base" />
                  ))}
                </div>
                <p className="text-white text-lg leading-relaxed mb-4 font-light">
                  &ldquo;{displayItems[featured]?.text}&rdquo;
                </p>
                <div>
                  <p className="text-white font-bold">{displayItems[featured]?.name}</p>
                  <p className="text-white/50 text-sm">{displayItems[featured]?.date}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial selector tiles */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {displayItems.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setFeatured(i)}
                className={`text-left p-5 rounded-2xl border transition-all duration-300 cursor-pointer ${featured === i ? 'bg-[#C70532]/20 border-[#C70532]/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
              >
                <div className="flex items-center gap-0.5 mb-2">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-amber-400 text-xs" />
                  ))}
                </div>
                <p className="text-white/80 text-xs leading-relaxed line-clamp-3 mb-3">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#03143A] border border-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">{t.initials}</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-xs">{t.name}</p>
                    <p className="text-white/40 text-xs">{t.date}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Testimonials CTA */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-8 bg-white/5 rounded-2xl border border-white/10">
            <div>
              <p className="text-white font-bold text-lg mb-1">Join 500+ Businesses That Trust Prime FSG</p>
              <p className="text-white/55 text-sm">Consistent quality, professional staff, and results you can count on every visit.</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 flex-wrap justify-center">
              <Link to="/contact" className="px-7 py-3 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
                Get a Free Quote
              </Link>
              <a href="tel:+17133382553" className="flex items-center gap-2 px-7 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full border border-white/20 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill" />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
